import { Injectable } from '@angular/core';

const MEBIBYTE = 1024 * 1024;

/**
 * Keeps support images useful while ensuring that only compressed WebP
 * derivatives are ever uploaded to the application's private asset storage.
 */
@Injectable({ providedIn: 'root' })
export class ImageOptimizationService {
  readonly maxSupportImages = 5;
  readonly maxSupportImageBytes = Math.floor(1.8 * MEBIBYTE);
  readonly maxSupportImageTotalBytes = 6 * MEBIBYTE;

  async optimizeSupportImage(source: File): Promise<File> {
    if (!source.type.startsWith('image/')) {
      throw new Error('Choose an image file.');
    }

    const image = await this.loadImage(source);
    const largestSide = Math.max(image.naturalWidth, image.naturalHeight);
    if (!largestSide) {
      throw new Error('This image could not be read. Choose another image.');
    }

    let scale = Math.min(1, 2_048 / largestSide);
    const qualities = [0.86, 0.78, 0.7, 0.62];

    for (let resizeAttempt = 0; resizeAttempt < 4; resizeAttempt += 1) {
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        throw new Error('Your browser could not prepare this screenshot.');
      }

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      for (const quality of qualities) {
        const blob = await this.toWebp(canvas, quality);
        if (blob.size <= this.maxSupportImageBytes) {
          return new File([blob], this.webPName(source.name), {
            type: 'image/webp',
            lastModified: Date.now(),
          });
        }
      }

      scale *= 0.78;
    }

    throw new Error('This image is too detailed to optimize below 2 MB. Crop it and try again.');
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('This image could not be decoded. Choose a PNG, JPEG, or WebP image.'));
      };
      image.src = url;
    });
  }

  private toWebp(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('This image could not be optimized.'));
      }, 'image/webp', quality);
    });
  }

  private webPName(fileName: string): string {
    const base = fileName.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '');
    return `${base || 'support-image'}.webp`;
  }
}
